import { useMemo } from "react";
import {
  calculateVolume,
  calculateEffectiveVolume,
  calculateTurnoverRate,
  getTurnoverStatus,
  litersToGallons,
  cmToInches,
  lphToGph,
  inchesToCm,
  gallonsToLiters,
  gphToLph,
  type TurnoverStatus,
} from "@/lib/tankCalculations";

/** 单位系统类型 */
export type UnitSystem = "metric" | "imperial";

/** Hook 输入参数 */
export interface VolumeCalculatorInput {
  /** 长度（当前单位系统下的值） */
  length: number;
  /** 宽度（当前单位系统下的值） */
  width: number;
  /** 高度（当前单位系统下的值） */
  height: number;
  /** 实际水位高度（当前单位系统下的值），可选 */
  actualWaterLevel?: number | null;
  /** 水泵流量（当前单位系统下的值），可选 */
  pumpFlowRate?: number | null;
  /** 单位系统 */
  unitSystem: UnitSystem;
}

/** Hook 返回结果 */
export interface VolumeCalculatorResult {
  /** 总容积（当前单位系统下的值） */
  totalVolume: number;
  /** 有效水量（当前单位系统下的值） */
  effectiveVolume: number;
  /** 循环倍率（次/小时） */
  turnoverRate: number | null;
  /** 循环倍率状态 */
  turnoverStatus: TurnoverStatus | null;
  /** 容积单位标签 */
  volumeUnit: string;
  /** 尺寸单位标签 */
  dimensionUnit: string;
  /** 流量单位标签 */
  flowRateUnit: string;
  /** 公制下的总容积（用于存储） */
  totalVolumeMetric: number;
  /** 公制下的有效水量（用于存储） */
  effectiveVolumeMetric: number;
}

/**
 * 鱼缸水量计算器 Hook
 *
 * 接收尺寸、水位、流量和单位系统作为输入，
 * 返回计算结果、状态和格式化的单位标签。
 * 使用 useMemo 优化性能，避免不必要的重复计算。
 *
 * @param input 计算器输入参数
 * @returns 计算结果
 *
 * @example
 * ```tsx
 * const result = useVolumeCalculator({
 *   length: 60,
 *   width: 30,
 *   height: 40,
 *   actualWaterLevel: 35,
 *   pumpFlowRate: 500,
 *   unitSystem: 'metric'
 * });
 *
 * console.log(result.totalVolume); // 72 (升)
 * console.log(result.effectiveVolume); // 63 (升)
 * console.log(result.turnoverRate); // 7.94 (次/小时)
 * console.log(result.turnoverStatus); // 'normal'
 * ```
 */
export function useVolumeCalculator(
  input: VolumeCalculatorInput,
): VolumeCalculatorResult {
  const { length, width, height, actualWaterLevel, pumpFlowRate, unitSystem } =
    input;

  // 使用 useMemo 缓存计算结果
  return useMemo(() => {
    // 单位标签
    const volumeUnit = unitSystem === "metric" ? "L" : "gal";
    const dimensionUnit = unitSystem === "metric" ? "cm" : "in";
    const flowRateUnit = unitSystem === "metric" ? "L/h" : "GPH";

    // 将输入值转换为公制（内部计算始终使用公制）
    const lengthCm = unitSystem === "metric" ? length : inchesToCm(length);
    const widthCm = unitSystem === "metric" ? width : inchesToCm(width);
    const heightCm = unitSystem === "metric" ? height : inchesToCm(height);
    const waterLevelCm =
      actualWaterLevel != null
        ? unitSystem === "metric"
          ? actualWaterLevel
          : inchesToCm(actualWaterLevel)
        : null;
    const flowRateLph =
      pumpFlowRate != null
        ? unitSystem === "metric"
          ? pumpFlowRate
          : gphToLph(pumpFlowRate)
        : null;

    // 计算公制下的容积
    const totalVolumeMetric = calculateVolume(lengthCm, widthCm, heightCm);
    const effectiveVolumeMetric = calculateEffectiveVolume(
      lengthCm,
      widthCm,
      waterLevelCm,
      heightCm,
    );

    // 计算循环倍率（基于公制有效水量）
    const turnoverRate = calculateTurnoverRate(
      effectiveVolumeMetric,
      flowRateLph,
    );
    const turnoverStatus = getTurnoverStatus(turnoverRate);

    // 根据单位系统转换输出值
    const totalVolume =
      unitSystem === "metric"
        ? totalVolumeMetric
        : litersToGallons(totalVolumeMetric);
    const effectiveVolume =
      unitSystem === "metric"
        ? effectiveVolumeMetric
        : litersToGallons(effectiveVolumeMetric);

    return {
      totalVolume,
      effectiveVolume,
      turnoverRate,
      turnoverStatus,
      volumeUnit,
      dimensionUnit,
      flowRateUnit,
      totalVolumeMetric,
      effectiveVolumeMetric,
    };
  }, [length, width, height, actualWaterLevel, pumpFlowRate, unitSystem]);
}

/** localStorage 存储键 */
const UNIT_PREFERENCE_KEY = "tank_unit_preference";

/**
 * 获取用户单位偏好
 * @returns 用户保存的单位系统，默认为公制
 */
export function getUnitPreference(): UnitSystem {
  if (typeof window === "undefined") {
    return "metric";
  }
  const saved = localStorage.getItem(UNIT_PREFERENCE_KEY);
  if (saved === "imperial" || saved === "metric") {
    return saved;
  }
  return "metric";
}

/**
 * 保存用户单位偏好
 * @param unit 单位系统
 */
export function setUnitPreference(unit: UnitSystem): void {
  if (typeof window !== "undefined") {
    localStorage.setItem(UNIT_PREFERENCE_KEY, unit);
  }
}
